/**
 * Dados da Central de Projetos para a lista de Clientes, por número de pedido.
 *
 * Só quem tem `central_projetos:view` consulta; para os demais (e com a
 * Central desligada) devolve `pedidos: null` e a lista mostra só a planilha.
 * A resposta fica em memória pela sessão: voltar da ficha para a lista não
 * pisca nem repete a consulta.
 */
import { useEffect, useState } from "react";

import { MODULO_CENTRAL_PROJETOS } from "../../../../config/modulos";
import { useAuth } from "../../../../context/AuthContext";
import { lerCarteira } from "../../../../services/centralProjetos";
import { codigoTenantCorrente } from "../../../../utils/tenantStorage";

export const PERMISSAO_CENTRAL = "central_projetos:view";

/** Última resposta por unidade: trocar de unidade nunca mostra a carteira da outra. */
const emMemoria = new Map();

export default function useCentralDaCarteira() {
  const { request, hasPermission, temModulo } = useAuth();
  // Central só com o módulo contratado (ADR-0102); sem ele a lista fica só com a planilha.
  const podeVer = Boolean(hasPermission?.(PERMISSAO_CENTRAL)) && Boolean(temModulo?.(MODULO_CENTRAL_PROJETOS));
  const tenant = codigoTenantCorrente();
  const [estado, setEstado] = useState(() => ({
    pedidos: podeVer ? emMemoria.get(tenant) ?? null : null,
    carregando: podeVer && !emMemoria.has(tenant),
  }));

  useEffect(() => {
    if (!podeVer) {
      setEstado({ pedidos: null, carregando: false });
      return undefined;
    }
    let vivo = true;
    lerCarteira(request)
      .then((resposta) => {
        const pedidos = resposta?.pedidos || {};
        emMemoria.set(tenant, pedidos);
        if (vivo) setEstado({ pedidos, carregando: false });
      })
      .catch(() => {
        // Central desligada (409) ou fora do ar: a lista segue só com a planilha.
        if (vivo) setEstado({ pedidos: null, carregando: false });
      });
    return () => {
      vivo = false;
    };
  }, [podeVer, request, tenant]);

  return estado;
}
